import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useAuth } from "../context/AuthContext";
import { SERVER_BASE_URL } from "../config";

function MailSettingsModal({ onClose }) {
  const { user } = useAuth();
  const [fromPrefix, setFromPrefix] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [subject, setSubject] = useState("");
  const [template, setTemplate] = useState("");
  const domainPart = "@juza.pro";

  const defaultTemplate = `{{ invitation.name }},

you are invited to add {{ invitation.guests }} guests to the guest list for "{{ event.title }}".

Where:
{{ event.venue_name }}
{% if event.venue_address and event.venue_address != '' %}
{{ event.venue_address }}
{% endif %}

When:
{{ event.starts | datetime("%A, %d.%m.%Y, %H:%M") }}{% if event.multiday %}
 - {{ event.end_time | datetime("%A, %d.%m.%Y") }}{% endif %}

Please use this link to enter your guests:
{{ link }}

Best regards,
{{ we.organization_name }}`

  useEffect(() => {
    const fetchMailSettings = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${SERVER_BASE_URL}/api/users/me/mail_settings`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          const fullFromAddress = data.fromAddress || "";
          const prefix = fullFromAddress.split("@")[0] || "";
          setFromPrefix(prefix);
          setReplyTo(data.replyTo || "");
          setSubject(data.subject || "");
          setTemplate(data.template || defaultTemplate);
        } else {
          console.error("Failed to load mail settings");
          const defaultPrefix = getDefaultFromPrefix(user?.organization);
          setFromPrefix(defaultPrefix);
          setReplyTo("");
          setSubject("");
          setTemplate(defaultTemplate);
        }
      } catch (error) {
        console.error("Error loading mail settings:", error);
        const defaultPrefix = getDefaultFromPrefix(user?.organization);
        setFromPrefix(defaultPrefix);
        setReplyTo("");
        setSubject("");
        setTemplate(defaultTemplate);
      }
    };

    fetchMailSettings();
  }, [user, defaultTemplate]);

  const getDefaultFromPrefix = (organizationName) => {
    if (organizationName) {
      const sanitizedOrgName = organizationName.toLowerCase().replace(/[^a-z0-9]/g, '');
      return `${sanitizedOrgName}-guest-list`;
    } else {
      return "no-reply";
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem("token");
      const fullFromAddress = fromPrefix + domainPart;
      const response = await fetch(
        `${SERVER_BASE_URL}/api/users/me/mail_settings`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fromAddress: fullFromAddress,
            replyTo,
            subject,
            template,
          }),
        }
      );
      if (response.ok) {
        onClose();
      } else {
        console.error("Failed to save mail settings");
        alert("Failed to save mail settings");
      }
    } catch (error) {
      console.error("Error saving mail settings:", error);
      alert("Error saving mail settings");
    }
  };

  const handleFromPrefixChange = (e) => {
    setFromPrefix(e.target.value);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-lg relative">
        <h2 className="text-xl font-bold mb-4 text-gray-800">Mail Settings</h2>

        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-700">From</label>
          <div className="flex">
            <input
              type="text"
              value={fromPrefix}
              onChange={handleFromPrefixChange}
              className="w-full border border-gray-300 p-2 rounded-l-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Например, hollystone-guest-list"
            />
            <span className="inline-flex items-center px-3 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-500 text-sm">
              {domainPart}
            </span>
          </div>
        </div>

        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-700">Reply-To</label>
          <input
            type="email"
            value={replyTo}
            onChange={(e) => setReplyTo(e.target.value)}
            className="w-full border border-gray-300 p-2 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Например, info@hollystonerecords.com"
          />
        </div>

        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-700">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full border border-gray-300 p-2 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            placeholder='Например, Guest list invitation for "{{ event.title }}"'
          />
        </div>

        <div className="mb-6">
          <label className="block mb-2 text-sm font-medium text-gray-700">Email Template</label>
          <textarea
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            rows={10}
            className="w-full border border-gray-300 p-2 rounded-md focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
          />
        </div>

        <div className="flex justify-end space-x-4">
          <button 
            onClick={onClose} 
            className="px-6 py-2 bg-white text-gray-700 font-medium rounded hover:bg-gray-100 border border-gray-300 transition duration-200"
          >
            Cancel
          </button>
          <button 
            onClick={handleSave} 
            className="px-6 py-2 bg-indigo-600 text-white font-medium rounded hover:bg-indigo-700 transition duration-200"
          >
            Save
          </button>
        </div>

        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 transition duration-200"
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

MailSettingsModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default MailSettingsModal;